import React, { useEffect } from 'react';
import { PageReadOnly as PageBlocks } from '@eaco/forms-ui-web/readonly';
import { usePageFiles } from '@/lib/usePageFiles';
import { renderPortalBlock } from '@/lib/portalPageBlocks';
import {
  coverImageUrl,
  coverBackground,
  coverPositionY,
  type PageMeta,
} from './PageReadOnly';

/**
 * Print-oriented read-only render of a full page document — every page's blocks,
 * on a plain white background, with a page break between editor pages so the
 * headless-Chromium `page.pdf()` produces one PDF page per editor page. Backs
 * the "Download PDF" action (`/p/preview/:oid/:token?mode=pdf`). Blocks render
 * through PageReadOnly in print mode: videos as still frames, toggles open.
 */

export interface PrintPage {
  elements: any[];
}

interface Props {
  name: string;
  meta: PageMeta;
  pages: PrintPage[];
  /** Called once every page's files have resolved, for the PDF render. */
  onReady?: () => void;
}

export const PagePrint: React.FC<Props> = ({ name, meta, pages, onReady }) => {
  const { resolveFile, ready } = usePageFiles(pages);
  useEffect(() => {
    if (ready) onReady?.();
  }, [ready, onReady]);
  const icon = meta?.icon;
  const cover = meta?.cover;
  const coverImg = coverImageUrl(cover);
  const coverBg = coverBackground(cover);
  const coverPosY = coverPositionY(cover);
  return (
    <div style={{ background: '#fff', color: '#1f2a3d', padding: '4px 0' }}>
      {pages.map((pg, i) => (
        <section
          key={i}
          style={{
            // One PDF page per editor page (last one flows naturally).
            breakAfter: i < pages.length - 1 ? 'page' : 'auto',
          }}
        >
          {i === 0 && (
            <header style={{ marginBottom: 18 }}>
              {(coverImg || coverBg) && (
                <div
                  style={{
                    height: 190,
                    marginBottom: 18,
                    borderRadius: 10,
                    overflow: 'hidden',
                    background: coverBg,
                  }}
                >
                  {coverImg && (
                    <img
                      src={coverImg}
                      alt=""
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: `center ${coverPosY}%`,
                        display: 'block',
                      }}
                    />
                  )}
                </div>
              )}
              {icon && (
                <div style={{ fontSize: 56, lineHeight: 1, marginBottom: 8 }}>
                  {icon}
                </div>
              )}
              <h1
                style={{
                  fontSize: 34,
                  fontWeight: 800,
                  lineHeight: 1.15,
                  letterSpacing: '-0.02em',
                  margin: 0,
                }}
              >
                {name || 'Untitled'}
              </h1>
            </header>
          )}
          <PageBlocks content={{ elements: pg.elements }} resolveFile={resolveFile} renderBlock={renderPortalBlock} print />
        </section>
      ))}
    </div>
  );
};

export default PagePrint;
